import * as React from "react";
import { Button, type ButtonProps } from "./Button";
import { cn } from "../../lib/utils";

export interface SubmitButtonProps extends Omit<ButtonProps, "loading"> {
  /** In-flight state (from useSubmittingMutation). Disables the button,
   * shows the inline spinner, and swaps the label. */
  pending: boolean;
  /** Label rendered while pending. Defaults to "Submitting…". */
  pendingLabel?: React.ReactNode;
}

/** Drop-in Button for submit actions: when `pending`, it disables itself,
 * renders the inline spinner, and swaps to `pendingLabel`. Layout-identical
 * to Button so it can replace one in any actions row. */
export const SubmitButton = React.forwardRef<HTMLButtonElement, SubmitButtonProps>(
  ({ pending, pendingLabel = "Submitting…", children, ...props }, ref) => (
    <Button ref={ref} loading={pending} {...props}>
      {pending ? pendingLabel : children}
    </Button>
  )
);
SubmitButton.displayName = "SubmitButton";

export interface SubmittingHintProps {
  pending: boolean;
  /** Warm, brand-consistent wait copy. */
  message?: string;
  className?: string;
}

export const SUBMITTING_HINT_DEFAULT = "Submitting — this can take a moment. Thanks for your patience.";

/** Muted helper line shown while a submission is in flight. Renders null
 * when idle; announces politely for screen readers. Place after the
 * actions row. */
export function SubmittingHint({ pending, message = SUBMITTING_HINT_DEFAULT, className }: SubmittingHintProps) {
  if (!pending) return null;
  return (
    <p role="status" aria-live="polite" className={cn("text-caption text-gray-mid", className)}>
      {message}
    </p>
  );
}
